/**
 * Peças comuns às telas de cobrança (Inadimplência e Pendente revistoria):
 * contato com o associado, blocos do detalhe e etiquetas.
 */

import { useState, type ReactNode } from 'react';
import { MessageCircle, Phone } from 'lucide-react';
import { formatarCpf, formatarTelefone } from '../../lib/format.ts';
import { cx } from '../../lib/cx.ts';
import { Segmentado } from '../../components/ui/index.tsx';

export interface Associado {
  codigo: string | null;
  nome: string | null;
  cpf: string | null;
  celular: string | null;
  telefone: string | null;
  email: string | null;
  cidade: string | null;
  estado: string | null;
  bairro: string | null;
}

export const dataBr = (iso: string | null | undefined) =>
  iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : null;

/** "ago/26" a partir de "2026-08" — rótulo de mês que cabe em coluna estreita. */
export function mesCurto(anoMes: string): string {
  const [ano, mes] = anoMes.split('-');
  const d = new Date(Number(ano), Number(mes) - 1, 1);
  return `${d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')}/${ano!.slice(2)}`;
}

/**
 * Conversa no WhatsApp com o celular do associado. Só celular: telefone fixo
 * não tem WhatsApp, e o link abriria uma conversa que não existe. O SGA grava
 * sem DDI, então o 55 entra quando falta.
 */
export function linkWhatsapp(celular: string | null): string | null {
  const d = (celular ?? '').replace(/\D/g, '');
  if (d.length === 10 || d.length === 11) return `https://wa.me/55${d}`;
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) return `https://wa.me/${d}`;
  return null;
}

/** Ligar e WhatsApp, com alvo de 44px — o polegar na rua. */
export function BotoesContato({ associado: a, compacto }: { associado: Associado; compacto?: boolean }) {
  const tel = a.celular ?? a.telefone;
  const zap = linkWhatsapp(a.celular);
  if (!tel && !zap) return null;

  const botao =
    'anel-foco inline-flex h-11 items-center justify-center gap-2 rounded-[var(--r-md)] border border-[var(--border-strong)] bg-[var(--surface)] px-4 text-[13px] font-medium text-[var(--fg)] hover:bg-[var(--surface-hover)]';

  return (
    <div className={cx('flex gap-2', compacto ? 'mt-3' : 'flex-wrap')}>
      {tel && (
        <a
          href={`tel:${tel.replace(/\D/g, '')}`}
          className={cx(botao, compacto && 'min-w-0 flex-1 px-3')}
        >
          <Phone size={15} className="shrink-0" />
          <span className="truncate">{compacto ? tel : 'Ligar'}</span>
        </a>
      )}
      {zap && (
        <a href={zap} target="_blank" rel="noreferrer" className={cx(botao, 'shrink-0')}>
          <MessageCircle size={15} /> WhatsApp
        </a>
      )}
    </div>
  );
}

/**
 * Detalhe em abas. Três colunas de "rótulo · valor" num modal de 768px
 * espremiam cada valor em 130px — CPF, e-mail e modelo quebravam em três
 * linhas. Em abas, cada grupo tem a largura inteira e o rótulo vai em cima do
 * valor, que é como se lê ficha em qualquer tela.
 */
export function AbasDetalhe({ abas }: { abas: { id: string; rotulo: string; conteudo: ReactNode }[] }) {
  const [aba, setAba] = useState(abas[0]!.id);
  const atual = abas.find((a) => a.id === aba) ?? abas[0]!;
  return (
    <div className="flex flex-col gap-4">
      <div className="scroll-x">
        <Segmentado
          rotulo="Seção do detalhe"
          valor={atual.id}
          onChange={setAba}
          opcoes={abas.map((a) => ({ valor: a.id, rotulo: a.rotulo }))}
        />
      </div>
      <div role="tabpanel">{atual.conteudo}</div>
    </div>
  );
}

/** Grade de campos da ficha: duas colunas a partir de 640px. */
export function GradeDados({ children }: { children: ReactNode }) {
  return <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">{children}</dl>;
}

export function Dado({
  rotulo,
  valor,
  num,
  largo,
}: {
  rotulo: string;
  valor: ReactNode;
  num?: boolean;
  /** Ocupa a linha inteira — endereço, modelo longo. */
  largo?: boolean;
}) {
  return (
    <div className={cx('min-w-0', largo && 'sm:col-span-2')}>
      <dt className="text-xs text-[var(--muted)]">{rotulo}</dt>
      <dd
        className={cx(
          'mt-0.5 text-[14px] text-[var(--fg)]',
          num ? 'num' : 'break-words',
        )}
      >
        {valor === null || valor === undefined || valor === '' ? (
          <span className="text-[var(--faint)]">—</span>
        ) : (
          valor
        )}
      </dd>
    </div>
  );
}

export function DadosAssociado({ associado: a }: { associado: Associado }) {
  return (
    <GradeDados>
      <Dado rotulo="Nome" valor={a.nome} largo />
      <Dado rotulo="CPF" valor={a.cpf ? formatarCpf(a.cpf) : null} num />
      <Dado rotulo="Código no SGA" valor={a.codigo} num />
      <Dado rotulo="Celular" valor={a.celular ? formatarTelefone(a.celular) : null} num />
      <Dado
        rotulo="Telefone"
        valor={a.telefone && a.telefone !== a.celular ? formatarTelefone(a.telefone) : null}
        num
      />
      <Dado rotulo="E-mail" valor={a.email} largo />
      <Dado rotulo="Bairro" valor={a.bairro} />
      <Dado rotulo="Cidade" valor={[a.cidade, a.estado].filter(Boolean).join(' / ') || null} />
    </GradeDados>
  );
}

export function DadosVeiculo({
  placa,
  veiculo,
  cooperativa,
  consultor,
}: {
  placa: string | null;
  veiculo: { marca: string | null; modelo: string | null; situacao: string | null };
  cooperativa: string | null;
  consultor: string | null;
}) {
  return (
    <GradeDados>
      <Dado rotulo="Placa" valor={placa} num />
      <Dado rotulo="Situação no SGA" valor={veiculo.situacao} />
      <Dado
        rotulo="Modelo"
        valor={[veiculo.marca, veiculo.modelo].filter(Boolean).join(' ') || null}
        largo
      />
      <Dado rotulo="Cooperativa" valor={cooperativa} />
      <Dado rotulo="Consultor" valor={consultor} />
    </GradeDados>
  );
}

export type TomEtiqueta = 'perigo' | 'alerta' | 'neutro';

const COR_ETIQUETA: Record<TomEtiqueta, string> = {
  perigo: 'var(--danger)',
  alerta: 'var(--warn)',
  neutro: 'var(--muted)',
};

export function Etiquetas({ itens }: { itens: { texto: string; tom: TomEtiqueta }[] }) {
  if (itens.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {itens.map((m) => (
        <span
          key={m.texto}
          className="rounded-[var(--r-full)] px-1.5 py-0.5 text-[10px] font-medium"
          style={{
            color: COR_ETIQUETA[m.tom],
            background: 'color-mix(in oklab, currentColor 12%, transparent)',
          }}
        >
          {m.texto}
        </span>
      ))}
    </div>
  );
}

/**
 * Linha de faixa clicável: rótulo, contagem, valor e a barra proporcional.
 * Usada nas faixas de atraso e nas de tempo em revistoria.
 */
export function LinhaFaixa({
  rotulo,
  placas,
  valor,
  fracao,
  cor,
  ativo,
  onClick,
}: {
  rotulo: string;
  placas: number;
  valor?: string;
  /** 0 a 1, para a largura da barra. */
  fracao: number;
  cor: string;
  ativo: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ativo}
      disabled={placas === 0}
      className={cx(
        'anel-foco grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 rounded-[var(--r-md)] border border-transparent px-2 py-2 text-left text-[13px] transition-colors enabled:hover:bg-[var(--surface-hover)] disabled:opacity-50',
        ativo && 'selecionado',
      )}
    >
      <span className="truncate text-[var(--fg)]">{rotulo}</span>
      <span className="num text-right whitespace-nowrap text-[var(--muted)]">
        <strong className="font-semibold text-[var(--fg)]">{placas}</strong>{' '}
        {placas === 1 ? 'placa' : 'placas'}
        {valor && <span className="ml-2">{valor}</span>}
      </span>
      <span className="col-span-2 h-2 overflow-hidden rounded-[var(--r-full)] bg-[var(--gray-100)]">
        <span
          className="block h-full rounded-[var(--r-full)]"
          style={{ width: `${Math.max(fracao * 100, placas > 0 ? 2 : 0)}%`, background: cor }}
        />
      </span>
    </button>
  );
}
